import React from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import {FastField} from "formik";

const styles = {
  childMenuItem: {
    pl: 5,
  },
  parentMenuItem: {
    color: "text.primary",
    opacity: 1,
  },
  tagsRoot: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    "& > *": {
      m: 0.5,
      mt: 1,
    },
  },
};

const GROUP_PREFIX = "__group__";
const isGroupKey = v => typeof v === "string" && v.startsWith(GROUP_PREFIX);

const renderProjectOptions = (options, fieldValue) =>
  options.map(([value, label]) => (
    <MenuItem key={value} value={value} sx={styles.childMenuItem}>
      <Checkbox checked={fieldValue.includes(value)} />
      <ListItemText primary={label} />
    </MenuItem>
  ));

const renderGroupOptions = (groups, fieldValue) =>
  groups.flatMap(([clientLabel, projects]) => {
    const projectIds   = projects.map(p => p[0]);
    const selectedCnt  = projectIds.filter(id => fieldValue.includes(id)).length;
    const allSelected  = selectedCnt === projectIds.length && selectedCnt > 0;
    const someSelected = selectedCnt > 0 && !allSelected;

    return [
      <MenuItem
        key={GROUP_PREFIX + clientLabel}
        value={GROUP_PREFIX + clientLabel}
        sx={styles.parentMenuItem}
      >
        <Checkbox checked={allSelected} indeterminate={someSelected} />
        <Typography variant="subtitle1" style={{ fontWeight: 500 }} color="textPrimary">
          {clientLabel}
        </Typography>
      </MenuItem>,
      ...renderProjectOptions(projects, fieldValue),
    ];
  });

const Select = ({
  options = [],
  groupOptions,
  label,
  InputLabelProps,
  ...props
}) => {
  const flatOptions = groupOptions ? groupOptions.flatMap(g => g[1]) : options;

  return (
    <FormControl fullWidth variant="outlined" size={props.size}>
      <FastField {...props}>
        {({ field, form, meta }) => {
          const error = meta.touched && meta.error;
          const value = field.value || [];

          const onChange = e => {
            let next  = e.target.value;
            const last = next[next.length - 1];

            if (isGroupKey(last)) {
              const label = last.replace(GROUP_PREFIX, "");
              const grp   = groupOptions.find(g => g[0] === label);
              const ids   = grp ? grp[1].map(p => p[0]) : [];
              const all   = ids.every(id => value.includes(id));

              next = all
                ? value.filter(id => !ids.includes(id))
                : [...value, ...ids.filter(id => !value.includes(id))];
            }

            next = next.filter(v => !isGroupKey(v));

            form.setFieldValue(field.name, next);
            props.onChange && props.onChange(e, form);
          };

          const labelFor = id =>
            (flatOptions.find(([val]) => val === id) || [null, ""])[1];

          return (
            <>
              <InputLabel {...InputLabelProps} id={field.name}>
                {label}
              </InputLabel>

              <MuiSelect
                multiple
                label={label}
                labelId={field.name}
                id={field.name}
                renderValue={selected => selected.map(labelFor).join(", ")}
                {...props}
                value={value}
                onChange={onChange}
              >
                {groupOptions
                  ? renderGroupOptions(groupOptions, value)
                  : renderProjectOptions(options, value)}
              </MuiSelect>

              {!groupOptions && (
                <Box sx={styles.tagsRoot}>
                  {value.map(v => (
                    <Chip
                      key={v}
                      label={labelFor(v)}
                      onDelete={() =>
                        form.setFieldValue(
                          field.name,
                          value.filter(item => item !== v)
                        )
                      }
                    />
                  ))}
                </Box>
              )}

              {error && <FormHelperText error>{error}</FormHelperText>}
            </>
          );
        }}
      </FastField>
    </FormControl>
  );
};

export default Select;

export const MultiEntitySelect = ({ suggestions = [], suggestionLabel = "name", ...rest }) => {
  const options = suggestions.map(suggestion => [suggestion.id, suggestion[suggestionLabel]]);

  return <Select options={options} {...rest} />;
};

